import React from "react";
import { Box, Collapse, Group, Stack, Select, Text } from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { ServiceAdjustmentForm } from "../../../../../../types/serviceAdjustmentForms";

interface AdditionalQuestionsSectionProps {
  serviceId: string;
  activeAdjustmentForms: ServiceAdjustmentForm[];
  selectedOptions: string[];
  onToggleOption: (optionId: string, questionId: string) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** Header presentation overrides (defaults preserve existing callers). */
  labelColor?: string;
  labelSize?: string;
  labelWeight?: number;
  iconSize?: number;
}

export default function AdditionalQuestionsSection({
  serviceId,
  activeAdjustmentForms,
  selectedOptions,
  onToggleOption,
  isExpanded,
  onToggleExpanded,
  labelColor = "#666",
  labelSize,
  labelWeight = 500,
  iconSize = 20,
}: AdditionalQuestionsSectionProps) {
  // Don't render if no forms available
  if (!serviceId || activeAdjustmentForms.length === 0) {
    return null;
  }

  return (
    <Box>
      <Group onClick={onToggleExpanded} style={{ cursor: "pointer" }} mb="sm">
        <Text c={labelColor} fw={labelWeight} size={labelSize}>
          Additional questions
        </Text>
        {isExpanded ? (
          <IconChevronUp size={iconSize} color={labelColor} />
        ) : (
          <IconChevronDown size={iconSize} color={labelColor} />
        )}
      </Group>
      <Collapse in={isExpanded}>
        <Stack gap="md">
          {activeAdjustmentForms.map((form) => (
            <Stack key={form.service_adjustment_form_id} gap="md">
              {form.questions.map((question) => {
                // Find the selected option for this question
                const selectedOptionForQuestion = question.options.find((opt) =>
                  selectedOptions.includes(opt.option_id!),
                );

                // Build options data for the Select component
                const selectOptions = question.options.map((option) => {
                  const deltaInfo = [];

                  // Build delta information display
                  if (option.price_delta && option.price_delta !== 0) {
                    deltaInfo.push(
                      `${option.price_delta > 0 ? "+" : ""}$${option.price_delta.toFixed(2)}`,
                    );
                  }
                  if (
                    option.duration_delta_mins &&
                    option.duration_delta_mins !== 0
                  ) {
                    deltaInfo.push(
                      `${option.duration_delta_mins > 0 ? "+" : ""}${option.duration_delta_mins} min`,
                    );
                  }

                  const label =
                    deltaInfo.length > 0
                      ? `${option.answer} (${deltaInfo.join(", ")})`
                      : option.answer;

                  return {
                    value: option.option_id!,
                    label: label,
                  };
                });

                return (
                  <Box key={question.question_id}>
                    <Select
                      label={question.question}
                      placeholder="Select an option"
                      data={selectOptions}
                      value={selectedOptionForQuestion?.option_id || null}
                      onChange={(value) => {
                        if (value) {
                          onToggleOption(value, question.question_id!);
                        } else {
                          // Handle deselection - pass empty string to remove selection
                          onToggleOption("", question.question_id!);
                        }
                      }}
                      clearable
                      searchable
                    />
                  </Box>
                );
              })}
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}
